import React from 'react';
import { Box, Button, List, Typography } from '@mui/material';

import { GeneratableBearing } from '@paragon/contract-library';
import { getHangerListsRecord } from '@paragon/material-lists';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getComponentDesignInsertionInfoOrNull } from '../../../../../../store/selectors/insertionObjectSelectors';
import { getInsertingProfileTrussSettings } from '../../../../../../store/selectors/profileSelectors';
import { startInsertBearingComponentInsertion } from '../../../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { BearingRow } from './BearingRow';

const getOverlaps = (bearings: readonly GeneratableBearing[], span: number) => {
  const getX = (bearing: GeneratableBearing) =>
    bearing.offsetSide === 'Left' ? bearing.offset : span - bearing.offset;
  const spans = bearings.map((bearing) =>
    bearing.generatableBearingType === 'VerticalGeneratableBearing'
      ? null
      : bearing.anchor === 'Center'
        ? {
            chord: bearing.chordLocation,
            start: getX(bearing) - bearing.width / 2,
            end: getX(bearing) + bearing.width / 2,
          }
        : bearing.anchor === 'Left'
          ? { chord: bearing.chordLocation, start: getX(bearing), end: getX(bearing) + bearing.width }
          : bearing.anchor === 'Right'
            ? { chord: bearing.chordLocation, start: getX(bearing) - bearing.width, end: getX(bearing) }
            : assertExhaustiveSwitch(bearing.anchor),
  );
  return spans.map((span, index) =>
    [...spans.slice(0, index), ...spans.slice(index + 1)].some(
      (otherSpan) =>
        span && otherSpan && span.start < otherSpan.end && otherSpan.start < span.end && span.chord === otherSpan.chord,
    ),
  );
};

const getOverlapMessage = (overlaps: readonly boolean[]) =>
  overlaps.some((value) => value)
    ? `These bearings overlap: ${overlaps
        .map((isOverlapping, index) => ({
          isOverlapping,
          index,
        }))
        .filter((pair) => pair.isOverlapping)
        .map((pair) => pair.index + 1)
        .join(', ')}`
    : '';

export const BearingList = () => {
  const dispatch = useAppDispatch();
  const { profile, span } = useAppSelector(getInsertingProfileTrussSettings);
  const { continuouslyBearing, generatableBearings } = profile;
  const overlaps = getOverlaps(generatableBearings, span);
  const componentDesignInsertionInfo = useAppSelector(getComponentDesignInsertionInfoOrNull);
  const hangerLists = useAppSelector(getHangerListsRecord);
  const bearings = generatableBearings.map((bearing, index) => (
    <BearingRow
      bearing={bearing}
      index={index}
      hangerMaterialList={
        componentDesignInsertionInfo && hangerLists
          ? hangerLists[componentDesignInsertionInfo.materialSelection.hangerListGuid]
          : undefined
      }
      key={bearing.guid}
    />
  ));
  return (
    <>
      {!continuouslyBearing && <List sx={{ m: 1 }}>{bearings}</List>}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1, alignItems: 'center' }}>
        <Button
          disabled={continuouslyBearing}
          variant="outlined"
          size="small"
          onClick={() => dispatch(startInsertBearingComponentInsertion())}
        >
          Add Bearing
        </Button>
        <Typography sx={{ m: 'auto' }} color="error">
          {continuouslyBearing
            ? ''
            : generatableBearings.length === 0
              ? 'Truss must have bearings.'
              : getOverlapMessage(overlaps)}
        </Typography>
      </Box>
    </>
  );
};
